import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/utils/apiConfig';

const ThemeContext = createContext();

const API = `${BACKEND_URL}/api`;

// Module-level cache to survive StrictMode remounts
let _cachedStyles = null;

const PAGES = ['home', 'map', 'dive_centers', 'packages', 'gallery', 'reviews', 'contact'];

// Read localStorage cache for instant first render
const getInitialStyles = () => {
  if (_cachedStyles) return _cachedStyles;
  try {
    const cached = localStorage.getItem('gd_page_styles');
    if (cached) {
      const parsed = JSON.parse(cached);
      _cachedStyles = parsed;
      return parsed;
    }
  } catch (e) {}
  return {};
};

// Load the user's saved theme-override id from localStorage.
const getInitialUserThemeId = () => {
  try {
    return localStorage.getItem('gd_user_theme_id') || null;
  } catch (e) {
    return null;
  }
};

// Text content fields that belong to the page, not the theme. When a user
// switches themes locally we MUST preserve these so the visible copy stays.
const TEXT_CONTENT_FIELDS = ['title', 'subtitle', 'description'];

// Apply theme visual styles onto a single component while preserving the
// page-specific text content (title/subtitle/description).
const overlayComponent = (themeComponent = {}, existingComponent = {}) => {
  const merged = { ...themeComponent };
  for (const field of TEXT_CONTENT_FIELDS) {
    if (existingComponent && existingComponent[field] != null) {
      merged[field] = existingComponent[field];
    }
  }
  return merged;
};

// Merge a theme preset's component styles over a page's existing styles,
// preserving any existing hero image/video media AND page-specific text.
const mergeThemeIntoPage = (themePreset, existingPageStyles) => {
  if (!themePreset) return existingPageStyles;
  const existing = existingPageStyles || {};

  // Header: preserve hero media + text content
  const header = overlayComponent(themePreset.header_style, existing.header);
  const existingHeader = existing.header;
  if (existingHeader && (existingHeader.background_type === 'image' || existingHeader.background_type === 'video')) {
    header.background_type = existingHeader.background_type;
    header.background_value = existingHeader.background_value;
    header.alt_text = existingHeader.alt_text;
    header.media_description = existingHeader.media_description;
    header.image_position = existingHeader.image_position || 'center';
    header.video_position = existingHeader.video_position || 'center';
    header.video_loop = existingHeader.video_loop !== false;
  }

  return {
    ...existing,
    navbar: overlayComponent(themePreset.navbar_style, existing.navbar),
    header,
    body: overlayComponent(themePreset.body_style, existing.body),
    footer: overlayComponent(themePreset.footer_style, existing.footer)
  };
};

// Apply a theme override onto an entire pageStyles dict.
const applyThemeToAllPages = (themePreset, pageStyles) => {
  if (!themePreset) return pageStyles;
  const result = {};
  for (const page of PAGES) {
    result[page] = mergeThemeIntoPage(themePreset, pageStyles[page]);
  }
  return result;
};

export const ThemeProvider = ({ children }) => {
  const [rawPageStyles, setRawPageStyles] = useState(getInitialStyles);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [userThemeId, setUserThemeIdState] = useState(getInitialUserThemeId);
  const [loading, setLoading] = useState(!_cachedStyles);
  const [currentPage, setCurrentPage] = useState('home');

  // Find the user-selected theme from the available list
  const userTheme = useMemo(
    () => availableThemes.find(t => t.id === userThemeId) || null,
    [availableThemes, userThemeId]
  );

  // Compute effective page styles: if user has an override, merge that theme's
  // styles onto every page (preserving existing hero media).
  const pageStyles = useMemo(() => {
    if (!userTheme) return rawPageStyles;
    return applyThemeToAllPages(userTheme, rawPageStyles);
  }, [rawPageStyles, userTheme]);

  // Persist effective page styles to localStorage so the FOUC script can read
  // the user's chosen look on the next page load.
  useEffect(() => {
    try {
      if (Object.keys(pageStyles).length > 0) {
        localStorage.setItem('gd_page_styles', JSON.stringify(pageStyles));
      }
    } catch (e) {}
  }, [pageStyles]);

  // Fetch styles for a specific page (from DB — raw, not overridden)
  const fetchPageStyles = useCallback(async (pageName) => {
    try {
      const response = await axios.get(`${API}/pages/${pageName}/styles`);
      setRawPageStyles(prev => {
        const updated = { ...prev, [pageName]: response.data };
        _cachedStyles = updated;
        return updated;
      });
    } catch (error) {
      console.error(`Error fetching styles for ${pageName}:`, error);
    }
  }, []);

  // Fetch all page styles + available themes on mount
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      // 1. Page styles
      const styles = {};
      for (const page of PAGES) {
        if (cancelled) return;
        try {
          const response = await axios.get(`${API}/pages/${page}/styles`);
          styles[page] = response.data;
        } catch (error) {
          if (_cachedStyles && _cachedStyles[page]) {
            styles[page] = _cachedStyles[page];
          }
        }
      }
      if (!cancelled) {
        _cachedStyles = styles;
        setRawPageStyles(styles);
        setLoading(false);
      }

      // 2. Public theme list (for navbar switcher)
      try {
        const res = await axios.get(`${API}/themes`);
        if (!cancelled) setAvailableThemes(res.data || []);
      } catch (e) {
        console.error('Failed to fetch public themes:', e);
      }
    };

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  // Get styles for the current page
  const getCurrentPageStyles = () => pageStyles[currentPage] || null;

  // Refresh styles (useful after admin updates)
  const refreshStyles = async (pageName) => {
    if (pageName) {
      await fetchPageStyles(pageName);
    } else {
      const styles = {};
      for (const page of PAGES) {
        try {
          const response = await axios.get(`${API}/pages/${page}/styles`);
          styles[page] = response.data;
        } catch (e) {}
      }
      _cachedStyles = styles;
      setRawPageStyles(styles);
    }
    // Also refresh available themes
    try {
      const res = await axios.get(`${API}/themes`);
      setAvailableThemes(res.data || []);
    } catch (e) {}
  };

  // Set the user's local theme override. Pass null to clear and fall back to admin default.
  const setUserTheme = useCallback((themeId) => {
    setUserThemeIdState(themeId);
    try {
      if (themeId) {
        localStorage.setItem('gd_user_theme_id', themeId);
      } else {
        localStorage.removeItem('gd_user_theme_id');
      }
    } catch (e) {}
  }, []);

  // Determine the currently-active theme: user override > admin default > first available
  const activeTheme = useMemo(() => {
    if (userTheme) return userTheme;
    const adminDefault = availableThemes.find(t => t.is_default);
    return adminDefault || availableThemes[0] || null;
  }, [userTheme, availableThemes]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      pageStyles,
      rawPageStyles,
      availableThemes,
      userThemeId,
      userTheme,
      activeTheme,
      setUserTheme,
      loading,
      currentPage,
      setCurrentPage,
      getCurrentPageStyles,
      refreshStyles,
      fetchPageStyles
    }),
    [pageStyles, rawPageStyles, availableThemes, userThemeId, userTheme, activeTheme, setUserTheme, loading, currentPage, fetchPageStyles]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
